import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import {
  Shield,
  DollarSign,
  BookOpen,
  Users,
  TrendingUp,
  Cpu,
  Image,
  Volume2,
  Film,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Crown,
  Calendar,
  Pencil,
  X,
  Plus,
  Ticket,
  Trash2,
  AlertTriangle,
  Search,
  Ban,
  Eye,
  Copy,
  Check,
  Sparkles,
  BarChart3,
  MousePointer,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import VersionBadge from '@/components/VersionBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL (handles both local /static/ paths and external URLs)
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Format currency with proper decimals
const formatCost = (cost) => {
  if (cost === null || cost === undefined) return '$0.00';
  return `$${cost.toFixed(4)}`;
};

// Format date to readable string
const formatDate = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format generation time in seconds to readable string (e.g., "5m 47s")
const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
  };

  return (
    <div className="rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{value}</p>
          {subValue && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subValue}</p>
          )}
        </div>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

// Cost breakdown bar
const CostBreakdownBar = ({ llm, images, audio, videos = 0, total }) => {
  if (!total || total === 0) return null;

  const llmPercent = (llm / total) * 100;
  const imagesPercent = (images / total) * 100;
  const audioPercent = (audio / total) * 100;
  const videosPercent = (videos / total) * 100;

  return (
    <div className="mt-4">
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
        <span>Cost Breakdown</span>
        <span>{formatCost(total)}</span>
      </div>
      <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
        <div
          className="bg-purple-500"
          style={{ width: `${llmPercent}%` }}
          title={`LLM: ${formatCost(llm)}`}
        />
        <div
          className="bg-blue-500"
          style={{ width: `${imagesPercent}%` }}
          title={`Images: ${formatCost(images)}`}
        />
        <div
          className="bg-green-500"
          style={{ width: `${audioPercent}%` }}
          title={`Audio: ${formatCost(audio)}`}
        />
        <div
          className="bg-amber-500"
          style={{ width: `${videosPercent}%` }}
          title={`Video: ${formatCost(videos)}`}
        />
      </div>
      <div className="flex justify-between text-xs mt-1">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-purple-500" />
          <span className="text-gray-500 dark:text-gray-400">LLM {formatCost(llm)}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-gray-500 dark:text-gray-400">Images {formatCost(images)}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-gray-500 dark:text-gray-400">Audio {formatCost(audio)}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-amber-500" />
          <span className="text-gray-500 dark:text-gray-400">Video {formatCost(videos)}</span>
        </div>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [stories, setStories] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [storiesPage, setStoriesPage] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [totalStories, setTotalStories] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pageSize = 20;

  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoPage, setPromoPage] = useState(0);
  const [totalPromoCodes, setTotalPromoCodes] = useState(0);

  // Failed jobs state
  const [failedJobs, setFailedJobs] = useState([]);
  const [failedPage, setFailedPage] = useState(0);
  const [totalFailedJobs, setTotalFailedJobs] = useState(0);
  const [selectedFailedJob, setSelectedFailedJob] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // User search state
  const [userSearch, setUserSearch] = useState('');

  // Version filter state (null = all, 1 = v1, 2 = v2, 3 = v3)
  const [versionFilter, setVersionFilter] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Handle user search with pagination reset
  const handleUserSearch = (value) => {
    setUserSearch(value);
    setUsersPage(0); // Reset to first page when searching
  };

  // Handle version filter change with pagination reset
  const handleVersionFilter = (version) => {
    setVersionFilter(version);
    setStoriesPage(0);
    setFailedPage(0);
  };

  // Modal states
  const [editingUser, setEditingUser] = useState(null);
  const [editCredits, setEditCredits] = useState(0);
  const [showCreatePromo, setShowCreatePromo] = useState(false);

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    if (activeTab === 'stories') {
      loadStories();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'promo') {
      loadPromoCodes();
    } else if (activeTab === 'failed') {
      loadFailedJobs();
    } else if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab, storiesPage, usersPage, promoPage, failedPage, userSearch, versionFilter]);

  const loadAdminData = async () => {
    try {
      const statsRes = await axios.get(`${API}/admin/stats`, {
        withCredentials: true
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics`, {
        withCredentials: true
      });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadStories = async () => {
    try {
      const params = { limit: pageSize, offset: storiesPage * pageSize };
      if (versionFilter !== null) {
        params.version = versionFilter;
      }
      const response = await axios.get(`${API}/admin/stories`, {
        params,
        withCredentials: true
      });
      setStories(response.data.stories);
      setTotalStories(response.data.total);
    } catch (error) {
      console.error('Failed to load stories:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const params = { limit: pageSize, offset: usersPage * pageSize };
      if (userSearch.trim()) {
        params.search = userSearch.trim();
      }
      const response = await axios.get(`${API}/admin/users`, {
        params,
        withCredentials: true
      });
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes`, {
        params: { limit: pageSize, offset: promoPage * pageSize },
        withCredentials: true
      });
      setPromoCodes(response.data.codes);
      setTotalPromoCodes(response.data.total);
    } catch (error) {
      console.error('Failed to load promo codes:', error);
    }
  };

  const loadFailedJobs = async () => {
    try {
      const params = { limit: pageSize, offset: failedPage * pageSize };
      if (versionFilter !== null) {
        params.version = versionFilter;
      }
      const response = await axios.get(`${API}/admin/failed-jobs`, {
        params,
        withCredentials: true
      });
      setFailedJobs(response.data.jobs);
      setTotalFailedJobs(response.data.total);
    } catch (error) {
      console.error('Failed to load failed jobs:', error);
    }
  };

  const clearAllFailedJobs = async () => {
    if (!window.confirm(`Are you sure you want to delete all ${totalFailedJobs} failed jobs? This cannot be undone.`)) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/admin/failed-jobs`, {
        withCredentials: true
      });
      toast.success(`Cleared ${response.data.deleted_count} failed jobs`);
      loadFailedJobs();
    } catch (error) {
      console.error('Failed to clear failed jobs:', error);
      toast.error('Failed to clear failed jobs');
    }
  };

  const deleteFailedJob = async (jobId) => {
    try {
      await axios.delete(`${API}/admin/failed-jobs/${jobId}`, {
        withCredentials: true
      });
      toast.success('Failed job deleted');
      loadFailedJobs();
      setSelectedFailedJob(null);
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error('Failed to delete job');
    }
  };

  // User Management
  const handleUpdateUser = async (userId, updates) => {
    try {
      const response = await axios.patch(`${API}/admin/users/${userId}`, updates, { withCredentials: true });
      toast.success('User updated');
      loadUsers();
      // Update editingUser with fresh data
      if (editingUser && editingUser.user_id === userId) {
        setEditingUser(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleAddCredits = (userId, amount) => {
    if (!amount || amount === 0) {
      toast.error('Enter a credit amount');
      return;
    }
    handleUpdateUser(userId, { credits: amount, credits_mode: 'add' });
    setEditCredits(0);
  };

  const togglePremium = (userId, newValue) => handleUpdateUser(userId, { is_premium: newValue });
  const toggleAdmin = (userId, newValue) => handleUpdateUser(userId, { is_admin: newValue });
  const toggleBanned = (userId, newValue) => handleUpdateUser(userId, { is_banned: newValue });

  // Promo Code Management
  const handleCreatePromo = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      code: formData.get('code'),
      credits: parseInt(formData.get('credits')) || 0,
      grants_premium: formData.get('grants_premium') === 'on',
      max_uses: formData.get('max_uses') ? parseInt(formData.get('max_uses')) : null
    };

    try {
      await axios.post(`${API}/admin/promo-codes`, data, { withCredentials: true });
      toast.success('Promo code created');
      setShowCreatePromo(false);
      loadPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const togglePromoActive = async (code) => {
    const promo = promoCodes.find(p => p.code === code);
    try {
      await axios.patch(`${API}/admin/promo-codes/${code}`,
        { is_active: !promo.is_active },
        { withCredentials: true }
      );
      loadPromoCodes();
      toast.success(`Promo code ${promo.is_active ? 'disabled' : 'enabled'}`);
    } catch (error) {
      toast.error('Failed to update promo code');
    }
  };

  const deletePromoCode = async (code) => {
    if (!window.confirm(`Delete promo code ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${code}`, { withCredentials: true });
      toast.success('Promo code deleted');
      loadPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAdminData();
    if (activeTab === 'stories') await loadStories();
    if (activeTab === 'users') await loadUsers();
    if (activeTab === 'promo') await loadPromoCodes();
    if (activeTab === 'failed') await loadFailedJobs();
    if (activeTab === 'analytics') await loadAnalytics();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Sparkles className="w-10 h-10 animate-spin" style={{ color: 'var(--st-primary)' }} />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl w-full mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Admin Panel</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">System overview and cost tracking</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors"
            style={{ background: 'var(--st-bg-surface)' }}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Tab Navigation - Scrollable on mobile */}
        <div className="overflow-x-auto -mx-4 px-4 mb-6">
          <div className="flex gap-1 min-w-max border-b border-gray-200 dark:border-gray-700">
            {['overview', 'analytics', 'stories', 'failed', 'users', 'promo'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 md:px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab
                    ? tab === 'failed' ? 'border-red-500 text-red-600'
                    : tab === 'analytics' ? 'border-blue-500 text-blue-600'
                    : 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {tab === 'failed' ? (
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Failed
                  </span>
                ) : tab === 'analytics' ? (
                  <span className="flex items-center gap-1">
                    <BarChart3 className="w-4 h-4" />
                    Analytics
                  </span>
                ) : (
                  tab
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={BookOpen}
                label="Total Stories"
                value={stats.total_stories.toLocaleString()}
                subValue={`${stats.stories_today} today`}
                color="blue"
              />
              <StatCard
                icon={DollarSign}
                label="Total API Cost"
                value={formatCost(stats.total_cost)}
                subValue={`Avg: ${formatCost(stats.avg_cost)}/story`}
                color="green"
              />
              <StatCard
                icon={Users}
                label="Total Users"
                value={stats.total_users.toLocaleString()}
                subValue={`${stats.premium_users} premium`}
                color="purple"
              />
              <StatCard
                icon={Crown}
                label="Admin Users"
                value={stats.admin_users}
                subValue={`${stats.cost_audits_count || 0} stories tracked`}
                color="orange"
              />
            </div>

            {/* Cost Breakdown Card */}
            <div className="rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Cost Breakdown by Category</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <Cpu className="w-8 h-8 text-purple-500 dark:text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCost(stats.cost_breakdown?.llm)}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">LLM Costs</p>
                </div>
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Image className="w-8 h-8 text-blue-500 dark:text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCost(stats.cost_breakdown?.images)}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Image Generation</p>
                </div>
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <Volume2 className="w-8 h-8 text-green-500 dark:text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCost(stats.cost_breakdown?.audio)}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Audio TTS</p>
                </div>
                <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <Film className="w-8 h-8 text-amber-500 dark:text-amber-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCost(stats.cost_breakdown?.videos)}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Video Animation</p>
                </div>
              </div>
              <CostBreakdownBar
                llm={stats.cost_breakdown?.llm || 0}
                images={stats.cost_breakdown?.images || 0}
                audio={stats.cost_breakdown?.audio || 0}
                videos={stats.cost_breakdown?.videos || 0}
                total={stats.total_cost}
              />
            </div>

            {/* Version Breakdown Card */}
            <div className="rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Stories by Version</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-lg bg-gray-100 dark:bg-gray-800">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-xs font-bold px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">v1</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-500 dark:text-gray-400">{stats.version_stats?.v1?.count || 0}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Standard</p>
                  <p className="text-xs font-mono mt-1 text-gray-500 dark:text-gray-400">{formatCost(stats.version_stats?.v1?.cost)}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-xs font-bold px-2 py-1 rounded bg-blue-100 dark:bg-blue-800/50 text-blue-600 dark:text-blue-400">v2</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.version_stats?.v2?.count || 0}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Premium</p>
                  <p className="text-xs font-mono mt-1 text-blue-600 dark:text-blue-400">{formatCost(stats.version_stats?.v2?.cost)}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-xs font-bold px-2 py-1 rounded bg-purple-100 dark:bg-purple-800/50 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-700">v3</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.version_stats?.v3?.count || 0}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Pro</p>
                  <p className="text-xs font-mono mt-1 text-purple-600 dark:text-purple-400">{formatCost(stats.version_stats?.v3?.cost)}</p>
                </div>
              </div>
            </div>

            {/* Enhanced Pipeline Stats Card */}
            <div className="rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Story Pipeline</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-xs font-bold px-2 py-1 rounded bg-green-100 dark:bg-green-800/50 text-green-600 dark:text-green-400">✨ Enhanced</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.pipeline_stats?.enhanced || 0}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">IllustrationBible</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-gray-100 dark:bg-gray-800">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-xs font-bold px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">Standard</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-500 dark:text-gray-400">{stats.pipeline_stats?.standard || 0}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Basic Pipeline</p>
                </div>
              </div>

              {/* Generation Timing Stats */}
              {stats.timing_stats?.v2?.count > 0 && (
                <div className="border-t border-gray-100 dark:border-gray-700 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Enhanced Pipeline Timing</h4>
                  <div className="grid grid-cols-3 gap-3 text-center mb-4">
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Avg Time</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {Math.floor(stats.timing_stats.v2.avg_seconds / 60)}m {Math.round(stats.timing_stats.v2.avg_seconds % 60)}s
                      </p>
                    </div>
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Min</p>
                      <p className="text-lg font-bold text-green-600">
                        {Math.floor(stats.timing_stats.v2.min_seconds / 60)}m {Math.round(stats.timing_stats.v2.min_seconds % 60)}s
                      </p>
                    </div>
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Max</p>
                      <p className="text-lg font-bold text-orange-600">
                        {Math.floor(stats.timing_stats.v2.max_seconds / 60)}m {Math.round(stats.timing_stats.v2.max_seconds % 60)}s
                      </p>
                    </div>
                  </div>

                  {/* Step-by-step breakdown */}
                  {stats.timing_stats.v2.avg_step_times && Object.keys(stats.timing_stats.v2.avg_step_times).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Average Step Times (last 20 stories)</p>
                      {Object.entries(stats.timing_stats.v2.avg_step_times)
                        .sort((a, b) => b[1] - a[1])
                        .map(([step, time]) => (
                          <div key={step} className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 dark:text-gray-300 w-32 truncate">{step}</span>
                            <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-indigo-500 h-2 rounded-full"
                                style={{ width: `${Math.min(100, (time / 60) * 100)}%` }}
                              />
                            </div>
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400 w-12 text-right">{time}s</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : analytics ? (
              <>
                {/* Page Views Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard
                    icon={MousePointer}
                    label="Views Today"
                    value={analytics.page_views.today.toLocaleString()}
                    color="blue"
                  />
                  <StatCard
                    icon={BarChart3}
                    label="Views This Week"
                    value={analytics.page_views.this_week.toLocaleString()}
                    color="purple"
                  />
                  <StatCard
                    icon={Users}
                    label="Unique Visitors Today"
                    value={analytics.unique_visitors.today.toLocaleString()}
                    color="green"
                  />
                  <StatCard
                    icon={UserPlus}
                    label="Signups This Week"
                    value={analytics.signups.this_week.toLocaleString()}
                    color="orange"
                  />
                </div>

                {/* Popular Pages */}
                <div className="rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                    Popular Pages (Last 7 Days)
                  </h3>
                  <div className="space-y-3">
                    {analytics.popular_pages.length > 0 ? (
                      analytics.popular_pages.map((page, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                          <span className="text-sm text-gray-700 dark:text-gray-300 font-mono">{page.page}</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{page.views.toLocaleString()} views</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No page view data yet</p>
                    )}
                  </div>
                </div>

                {/* Daily Views Chart (Simple Bar Representation) */}
                <div className="rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                    Daily Page Views (Last 14 Days)
                  </h3>
                  <div className="flex items-end gap-1 h-32">
                    {analytics.daily_views.length > 0 ? (
                      analytics.daily_views.map((day, index) => {
                        const maxViews = Math.max(...analytics.daily_views.map(d => d.views), 1);
                        const height = (day.views / maxViews) * 100;
                        return (
                          <div
                            key={index}
                            className="flex-1 bg-blue-500 dark:bg-blue-600 rounded-t hover:bg-blue-600 dark:hover:bg-blue-500 transition-colors cursor-pointer group relative"
                            style={{ height: `${Math.max(height, 2)}%` }}
                            title={`${day.date}: ${day.views} views`}
                          >
                            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {day.date}: {day.views}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No data yet</p>
                    )}
                  </div>
                  {analytics.daily_views.length > 0 && (
                    <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>{analytics.daily_views[0]?.date}</span>
                      <span>{analytics.daily_views[analytics.daily_views.length - 1]?.date}</span>
                    </div>
                  )}
                </div>

                {/* Signups Over Time */}
                <div className="rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-green-500" />
                    User Signups (Last 30 Days)
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                      <p className="text-xs text-gray-500 dark:text-gray-400">This Week</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400">{analytics.signups.this_week}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                      <p className="text-xs text-gray-500 dark:text-gray-400">This Month</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400">{analytics.signups.this_month}</p>
                    </div>
                  </div>
                  <div className="flex items-end gap-1 h-24">
                    {analytics.signups.daily.length > 0 ? (
                      analytics.signups.daily.map((day, index) => {
                        const maxSignups = Math.max(...analytics.signups.daily.map(d => d.signups), 1);
                        const height = (day.signups / maxSignups) * 100;
                        return (
                          <div
                            key={index}
                            className="flex-1 bg-green-500 dark:bg-green-600 rounded-t hover:bg-green-600 dark:hover:bg-green-500 transition-colors cursor-pointer group relative"
                            style={{ height: `${Math.max(height, 2)}%` }}
                            title={`${day.date}: ${day.signups} signups`}
                          >
                            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {day.date}: {day.signups}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No signup data yet</p>
                    )}
                  </div>
                </div>

                {/* Daily Active Users */}
                {analytics.daily_active_users.length > 0 && (
                  <div className="rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-indigo-500" />
                      Daily Active Users (Logged In)
                    </h3>
                    <div className="space-y-2">
                      {analytics.daily_active_users.map((day, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{day.date}</span>
                          <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{day.users} users</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No analytics data available yet</p>
                <p className="text-sm mt-1">Page views will be tracked as users visit your site</p>
              </div>
            )}
          </div>
        )}

        {/* Stories Tab */}
        {activeTab === 'stories' && (
          <div className="space-y-4">
            {/* Version Filter Pills */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">Filter by version:</span>
              <button
                onClick={() => handleVersionFilter(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  versionFilter === null
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleVersionFilter(1)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  versionFilter === 1
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  background: versionFilter === 1 ? '#6B7280' : '#E8E8E8',
                  color: versionFilter === 1 ? 'white' : '#6B7280'
                }}
              >
                v1 Standard
              </button>
              <button
                onClick={() => handleVersionFilter(2)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  versionFilter === 2
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  background: versionFilter === 2 ? '#3B82F6' : 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
                  color: versionFilter === 2 ? 'white' : '#3B82F6'
                }}
              >
                v2 Premium
              </button>
              <button
                onClick={() => handleVersionFilter(3)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  versionFilter === 3
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  background: versionFilter === 3 ? '#7C3AED' : 'linear-gradient(135deg, #FDF4FF 0%, #F3E8FF 100%)',
                  color: versionFilter === 3 ? 'white' : '#7C3AED'
                }}
              >
                v3 Pro
              </button>
            </div>

            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {stories.map((story) => (
                <div
                  key={story.id}
                  className="rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow"
                  style={{ background: 'var(--st-bg-surface)' }}
                  onClick={() => navigate(`/story/${story.id}`)}
                >
                  <div className="flex items-start gap-3 mb-3">
                    {story.thumbnail ? (
                      <img
                        src={getImageUrl(story.thumbnail)}
                        alt={`Thumbnail for ${story.title}`}
                        className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{story.title}</p>
                        <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{story.author_name} • {story.page_count} pages</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{formatDate(story.created_at)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-6 gap-1 text-center">
                    <div className="p-1.5 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                      <p className="text-[10px] text-purple-600 dark:text-purple-400 font-mono font-medium">
                        {story.api_costs?.llm?.total ? formatCost(story.api_costs.llm.total) : '-'}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">LLM</p>
                    </div>
                    <div className="p-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                      <p className="text-[10px] text-blue-600 dark:text-blue-400 font-mono font-medium">
                        {story.api_costs?.images?.total ? formatCost(story.api_costs.images.total) : '-'}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">Images</p>
                    </div>
                    <div className="p-1.5 bg-green-50 dark:bg-green-900/30 rounded-lg">
                      <p className="text-[10px] text-green-600 dark:text-green-400 font-mono font-medium">
                        {story.api_costs?.audio?.total ? formatCost(story.api_costs.audio.total) : '-'}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">Audio</p>
                    </div>
                    <div className="p-1.5 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 font-mono font-medium">
                        {story.api_costs?.videos?.total ? formatCost(story.api_costs.videos.total) : '-'}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">Video</p>
                    </div>
                    <div className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <p className="text-[10px] text-gray-900 dark:text-gray-100 font-mono font-semibold">
                        {story.api_costs?.total ? formatCost(story.api_costs.total) : '-'}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">Total</p>
                    </div>
                    <div className="p-1.5 bg-orange-50 dark:bg-orange-900/30 rounded-lg">
                      <p className="text-[10px] text-orange-600 dark:text-orange-400 font-mono font-medium">
                        {formatTime(story.generation_time_seconds)}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">Time</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden" style={{ background: 'var(--st-bg-surface)' }}>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ minWidth: '950px', tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '220px' }}>Story</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '120px' }}>Author</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '80px' }}>LLM</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '80px' }}>Images</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '80px' }}>Audio</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '80px' }}>Video</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '80px' }}>Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '70px' }}>Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '100px' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {stories.map((story) => (
                      <tr
                        key={story.id}
                        className="hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
                        onClick={() => navigate(`/story/${story.id}`)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {story.thumbnail && (
                              <img
                                src={getImageUrl(story.thumbnail)}
                                alt={`Thumbnail for ${story.title}`}
                                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate max-w-[120px]">{story.title}</p>
                                <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{story.page_count} pages</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 truncate">{story.author_name}</td>
                        <td className="px-4 py-3 text-sm text-purple-600 font-mono">
                          {story.api_costs?.llm?.total ? formatCost(story.api_costs.llm.total) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-blue-600 font-mono">
                          {story.api_costs?.images?.total ? formatCost(story.api_costs.images.total) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-600 font-mono">
                          {story.api_costs?.audio?.total ? formatCost(story.api_costs.audio.total) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-amber-600 font-mono">
                          {story.api_costs?.videos?.total ? formatCost(story.api_costs.videos.total) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 font-mono">
                          {story.api_costs?.total ? formatCost(story.api_costs.total) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-orange-600 font-mono">
                          {formatTime(story.generation_time_seconds)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(story.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {storiesPage * pageSize + 1} - {Math.min((storiesPage + 1) * pageSize, totalStories)} of {totalStories}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setStoriesPage(Math.max(0, storiesPage - 1))}
                  disabled={storiesPage === 0}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setStoriesPage(storiesPage + 1)}
                  disabled={(storiesPage + 1) * pageSize >= totalStories}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Failed Jobs Tab */}
        {activeTab === 'failed' && (
          <div className="space-y-4">
            {/* Version Filter Pills */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">Filter by version:</span>
              <button
                onClick={() => handleVersionFilter(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  versionFilter === null
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleVersionFilter(1)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  versionFilter === 1
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  background: versionFilter === 1 ? '#6B7280' : '#E8E8E8',
                  color: versionFilter === 1 ? 'white' : '#6B7280'
                }}
              >
                v1 Standard
              </button>
              <button
                onClick={() => handleVersionFilter(2)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  versionFilter === 2
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  background: versionFilter === 2 ? '#3B82F6' : 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
                  color: versionFilter === 2 ? 'white' : '#3B82F6'
                }}
              >
                v2 Premium
              </button>
              <button
                onClick={() => handleVersionFilter(3)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  versionFilter === 3
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  background: versionFilter === 3 ? '#7C3AED' : 'linear-gradient(135deg, #FDF4FF 0%, #F3E8FF 100%)',
                  color: versionFilter === 3 ? 'white' : '#7C3AED'
                }}
              >
                v3 Pro
              </button>
            </div>

            {/* Header with Clear All button */}
            {totalFailedJobs > 0 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {totalFailedJobs} failed job{totalFailedJobs !== 1 ? 's' : ''}
                </p>
                <button
                  onClick={clearAllFailedJobs}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear All
                </button>
              </div>
            )}

            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {failedJobs.length === 0 ? (
                <div className="rounded-xl p-8 text-center shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
                  <AlertTriangle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400 dark:text-gray-400">No failed jobs found. That's good news!</p>
                </div>
              ) : (
                failedJobs.map((job) => (
                  <div
                    key={job.id}
                    className="rounded-xl p-4 shadow-sm border border-red-100 dark:border-red-900"
                    style={{ background: 'var(--st-bg-surface)' }}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      {job.thumbnail_url ? (
                        <img
                          src={getImageUrl(job.thumbnail_url)}
                          alt="Failed job thumbnail"
                          className="w-12 h-12 rounded-lg object-cover opacity-50 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm line-clamp-2">{job.prompt}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{job.user_name} • {job.age_range}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                        {job.step}
                      </span>
                      <VersionBadge version={job.story_version} isProStory={job.is_pro_story} size="small" />
                    </div>

                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg mb-3">
                      <p className="text-xs text-red-600 line-clamp-2">{job.error || 'Unknown error'}</p>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{formatDate(job.created_at)}</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedFailedJob(job)}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteFailedJob(job.id)}
                          className="p-2 rounded-lg hover:bg-red-100 text-gray-400 dark:text-gray-500 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden" style={{ background: 'var(--st-bg-surface)' }}>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ minWidth: '1000px', tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '250px' }}>Job</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '100px' }}>User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '100px' }}>Failed At</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '90px' }}>LLM Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '80px' }}>Images</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '150px' }}>Error</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '120px' }}>Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '70px' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {failedJobs.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          No failed jobs found. That's good news!
                        </td>
                      </tr>
                    ) : (
                      failedJobs.map((job) => (
                        <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-white/5">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {job.thumbnail_url ? (
                                <img
                                  src={getImageUrl(job.thumbnail_url)}
                                  alt="Failed job thumbnail"
                                  className="w-10 h-10 rounded-lg object-cover opacity-50"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                                  <AlertTriangle className="w-5 h-5 text-red-500" />
                                </div>
                              )}
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate max-w-[180px]">
                                    {job.prompt}
                                  </p>
                                  <VersionBadge version={job.story_version} isProStory={job.is_pro_story} size="small" />
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {job.age_range}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{job.user_name}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                              {job.step}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-purple-600 font-mono">
                            {job.api_costs?.llm?.total ? formatCost(job.api_costs.llm.total) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {job.pages_with_images || 0} / {job.total_pages || '?'}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-red-600 truncate max-w-[200px]" title={job.error}>
                              {job.error || 'Unknown error'}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(job.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setSelectedFailedJob(job)}
                                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                title="View details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteFailedJob(job.id)}
                                className="p-1.5 rounded-lg hover:bg-red-100 text-gray-400 dark:text-gray-500 hover:text-red-600 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalFailedJobs > pageSize && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {failedPage * pageSize + 1} - {Math.min((failedPage + 1) * pageSize, totalFailedJobs)} of {totalFailedJobs}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFailedPage(Math.max(0, failedPage - 1))}
                    disabled={failedPage === 0}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setFailedPage(failedPage + 1)}
                    disabled={(failedPage + 1) * pageSize >= totalFailedJobs}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Search bar */}
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={userSearch}
                onChange={(e) => handleUserSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm dark:bg-gray-800 dark:text-gray-100"
              />
              {userSearch && (
                <button
                  onClick={() => handleUserSearch('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {users.map((user) => (
                <div
                  key={user.user_id}
                  className="rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
                  style={{ background: 'var(--st-bg-surface)' }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img
                          src={getImageUrl(user.picture)}
                          alt={`${user.name} avatar`}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                          <Users className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{user.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px]">{user.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setEditingUser(user);
                        setEditCredits(0);
                      }}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:bg-purple-900/20 rounded-lg transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {user.is_banned && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
                        Banned
                      </span>
                    )}
                    {user.is_admin && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full">
                        Admin
                      </span>
                    )}
                    {user.is_premium && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                        Premium
                      </span>
                    )}
                    {!user.is_admin && !user.is_premium && !user.is_banned && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                        Free
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">{user.stories_generated}</p>
                      <p className="text-gray-500 dark:text-gray-400">Stories</p>
                    </div>
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">{user.credits}</p>
                      <p className="text-gray-500 dark:text-gray-400">Credits</p>
                    </div>
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                      <p className="text-gray-500 dark:text-gray-400">Joined</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden" style={{ background: 'var(--st-bg-surface)' }}>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ minWidth: '900px', tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '160px' }}>User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '220px' }}>Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '70px' }}>Stories</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '70px' }}>Credits</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '100px' }}>Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '100px' }}>Joined</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" style={{ width: '70px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {users.map((user) => (
                      <tr key={user.user_id} className="hover:bg-gray-50 dark:hover:bg-white/5">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img
                                src={getImageUrl(user.picture)}
                                alt={`${user.name} avatar`}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                                <Users className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                              </div>
                            )}
                            <span className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 truncate">{user.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{user.stories_generated}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{user.credits}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {user.is_banned && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
                                Banned
                              </span>
                            )}
                            {user.is_admin && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full">
                                Admin
                              </span>
                            )}
                            {user.is_premium && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                                Premium
                              </span>
                            )}
                            {!user.is_admin && !user.is_premium && !user.is_banned && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                                Free
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => {
                              setEditingUser(user);
                              setEditCredits(0);
                            }}
                            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:bg-purple-900/20 rounded-lg transition-colors"
                            title="Edit user"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {usersPage * pageSize + 1} - {Math.min((usersPage + 1) * pageSize, totalUsers)} of {totalUsers}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setUsersPage(Math.max(0, usersPage - 1))}
                  disabled={usersPage === 0}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setUsersPage(usersPage + 1)}
                  disabled={(usersPage + 1) * pageSize >= totalUsers}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promo' && (
          <div className="space-y-4">
            {/* Create button */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowCreatePromo(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Create</span> Promo Code
              </button>
            </div>

            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {promoCodes.length === 0 ? (
                <div className="rounded-xl p-8 text-center shadow-sm border border-gray-100 dark:border-gray-700" style={{ background: 'var(--st-bg-surface)' }}>
                  <Ticket className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400 dark:text-gray-400">No promo codes yet. Create one to get started.</p>
                </div>
              ) : (
                promoCodes.map((promo) => (
                  <div
                    key={promo.code}
                    className="rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
                  style={{ background: 'var(--st-bg-surface)' }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="font-mono font-bold text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded text-sm">
                          {promo.code}
                        </span>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            promo.is_active
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          }`}>
                            {promo.is_active ? 'Active' : 'Inactive'}
                          </span>
                          {promo.grants_premium && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                              Premium
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => togglePromoActive(promo.code)}
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                            promo.is_active
                              ? 'text-red-600 bg-red-50 dark:bg-red-900/20'
                              : 'text-green-600 bg-green-50 dark:bg-green-900/20'
                          }`}
                        >
                          {promo.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => deletePromoCode(promo.code)}
                          className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 bg-gray-50 dark:bg-gray-800 hover:bg-red-50 dark:bg-red-900/20 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">{promo.credits}</p>
                        <p className="text-gray-500 dark:text-gray-400">Credits</p>
                      </div>
                      <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">{promo.times_used} / {promo.max_uses || '∞'}</p>
                        <p className="text-gray-500 dark:text-gray-400">Uses</p>
                      </div>
                      <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{new Date(promo.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                        <p className="text-gray-500 dark:text-gray-400">Created</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden" style={{ background: 'var(--st-bg-surface)' }}>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ minWidth: '900px', tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Credits</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Premium</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Uses</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {promoCodes.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          No promo codes yet. Create one to get started.
                        </td>
                      </tr>
                    ) : (
                      promoCodes.map((promo) => (
                        <tr key={promo.code} className="hover:bg-gray-50 dark:hover:bg-white/5">
                          <td className="px-4 py-3">
                            <span className="font-mono font-medium text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                              {promo.code}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{promo.credits}</td>
                          <td className="px-4 py-3">
                            {promo.grants_premium ? (
                              <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                                Yes
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400 dark:text-gray-500">No</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {promo.times_used} / {promo.max_uses || '∞'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              promo.is_active
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            }`}>
                              {promo.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(promo.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              <button
                                onClick={() => togglePromoActive(promo.code)}
                                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                  promo.is_active
                                    ? 'text-red-600 hover:bg-red-50 dark:bg-red-900/20'
                                    : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                }`}
                              >
                                {promo.is_active ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                onClick={() => deletePromoCode(promo.code)}
                                className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:bg-red-900/20 rounded transition-colors"
                                title="Delete promo code"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPromoCodes > pageSize && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {promoPage * pageSize + 1} - {Math.min((promoPage + 1) * pageSize, totalPromoCodes)} of {totalPromoCodes}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPromoPage(Math.max(0, promoPage - 1))}
                    disabled={promoPage === 0}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPromoPage(promoPage + 1)}
                    disabled={(promoPage + 1) * pageSize >= totalPromoCodes}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditingUser(null)} />
          <div className="relative rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" style={{ background: 'var(--st-bg-surface)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Edit User</h3>
              <button
                onClick={() => setEditingUser(null)}
                className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User info */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
              {editingUser.picture ? (
                <img
                  src={getImageUrl(editingUser.picture)}
                  alt={`${editingUser.name} avatar`}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                  <Users className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{editingUser.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{editingUser.email}</p>
              </div>
            </div>

            {/* Credits adjustment */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Add Credits (current: {editingUser.credits})
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={editCredits}
                  onChange={(e) => setEditCredits(parseInt(e.target.value) || 0)}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-gray-100"
                  placeholder="Enter amount"
                />
                <button
                  onClick={() => handleAddCredits(editingUser.user_id, editCredits)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Premium toggle */}
            <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Premium Status</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {editingUser.is_premium ? 'User has premium access' : 'User is on free tier'}
                </p>
              </div>
              <button
                onClick={() => togglePremium(editingUser.user_id, !editingUser.is_premium)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  editingUser.is_premium
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200'
                }`}
              >
                {editingUser.is_premium ? 'Revoke' : 'Grant'}
              </button>
            </div>

            {/* Admin toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Admin Status</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {editingUser.is_admin ? 'User has admin access' : 'User is not an admin'}
                </p>
              </div>
              <button
                onClick={() => toggleAdmin(editingUser.user_id, !editingUser.is_admin)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  editingUser.is_admin
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200'
                }`}
              >
                {editingUser.is_admin ? 'Revoke' : 'Grant'}
              </button>
            </div>

            {/* Ban toggle - more prominent styling */}
            <div className={`flex items-center justify-between p-3 rounded-lg border-2 ${
              editingUser.is_banned
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200'
                : 'bg-gray-50 dark:bg-gray-800 border-transparent'
            }`}>
              <div className="flex items-center gap-2">
                <Ban className={`w-5 h-5 ${editingUser.is_banned ? 'text-red-600' : 'text-gray-400 dark:text-gray-500'}`} />
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">Account Access</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {editingUser.is_banned
                      ? 'User is banned and cannot access the platform'
                      : 'User has normal access'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (!editingUser.is_banned) {
                    if (window.confirm(`Are you sure you want to ban ${editingUser.name}? They will be logged out and unable to access the platform.`)) {
                      toggleBanned(editingUser.user_id, true);
                    }
                  } else {
                    toggleBanned(editingUser.user_id, false);
                  }
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  editingUser.is_banned
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200'
                }`}
              >
                {editingUser.is_banned ? 'Unban' : 'Ban User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Promo Code Modal */}
      {showCreatePromo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreatePromo(false)} />
          <div className="relative rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" style={{ background: 'var(--st-bg-surface)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Create Promo Code</h3>
              </div>
              <button
                onClick={() => setShowCreatePromo(false)}
                className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreatePromo} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Code
                </label>
                <input
                  name="code"
                  type="text"
                  required
                  placeholder="SUMMER2024"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 uppercase font-mono dark:bg-gray-800 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Will be stored in uppercase</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Credits to Grant
                </label>
                <input
                  name="credits"
                  type="number"
                  defaultValue={0}
                  min={0}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Max Uses
                </label>
                <input
                  name="max_uses"
                  type="number"
                  min={1}
                  placeholder="Leave blank for unlimited"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  name="grants_premium"
                  type="checkbox"
                  id="grants_premium"
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="grants_premium" className="text-sm text-gray-700 dark:text-gray-200">
                  Grant premium status when redeemed
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreatePromo(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Failed Job Details Modal */}
      {selectedFailedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setSelectedFailedJob(null); setCopiedField(null); }} />
          <div className="relative rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'var(--st-bg-surface)' }}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Failed Job Details</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Job ID: {selectedFailedJob.id}</p>
                </div>
              </div>
              <button
                onClick={() => { setSelectedFailedJob(null); setCopiedField(null); }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Job Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">User</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{selectedFailedJob.user_name}</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Failed Step</p>
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                    {selectedFailedJob.step}
                  </span>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Age Range</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{selectedFailedJob.age_range}</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Version</p>
                  <VersionBadge version={selectedFailedJob.story_version} isProStory={selectedFailedJob.is_pro_story} />
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Images Progress</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{selectedFailedJob.pages_with_images || 0} / {selectedFailedJob.total_pages || '?'}</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">LLM Cost</p>
                  <p className="font-medium text-purple-600 font-mono">
                    {selectedFailedJob.api_costs?.llm?.total ? formatCost(selectedFailedJob.api_costs.llm.total) : '-'}
                  </p>
                </div>
              </div>

              {/* Full Prompt */}
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">Full Prompt</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedFailedJob.full_prompt || selectedFailedJob.prompt);
                      setCopiedField('prompt');
                      setTimeout(() => setCopiedField(null), 2000);
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                    title="Copy to clipboard"
                  >
                    {copiedField === 'prompt' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{selectedFailedJob.full_prompt || selectedFailedJob.prompt}</p>
              </div>

              {/* User-Friendly Error */}
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800">
                <p className="text-xs text-red-600 uppercase mb-2">Error Message</p>
                <p className="text-sm text-red-800">{selectedFailedJob.error || 'Unknown error'}</p>
              </div>

              {/* Detailed Error Info */}
              {selectedFailedJob.error_details && (
                <>
                  {/* Pipeline Progress Info */}
                  <div className="grid grid-cols-2 gap-3">
                    {selectedFailedJob.error_details.failed_step && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800">
                        <p className="text-xs text-red-600 uppercase mb-1">Failed At Step</p>
                        <code className="text-sm text-red-800 font-mono">{selectedFailedJob.error_details.failed_step}</code>
                      </div>
                    )}
                    {selectedFailedJob.error_details.last_successful_step && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800">
                        <p className="text-xs text-green-600 uppercase mb-1">Last Successful Step</p>
                        <code className="text-sm text-green-800 font-mono">{selectedFailedJob.error_details.last_successful_step}</code>
                      </div>
                    )}
                  </div>

                  {/* Progress at failure */}
                  {selectedFailedJob.error_details.progress && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                      <p className="text-xs text-blue-600 uppercase mb-2">Progress at Failure</p>
                      <div className="flex gap-4 text-sm">
                        <span className="text-blue-800">
                          Pages: <code className="font-mono">{selectedFailedJob.error_details.progress.pages_generated || 0}</code>
                        </span>
                        <span className="text-blue-800">
                          Images: <code className="font-mono">{selectedFailedJob.error_details.progress.images_generated || 0}</code>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Job Parameters */}
                  {selectedFailedJob.error_details.job_params && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-2">Job Parameters</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {selectedFailedJob.error_details.job_params.age_range && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Age Range: </span>
                            <code className="text-gray-800">{selectedFailedJob.error_details.job_params.age_range}</code>
                          </div>
                        )}
                        {selectedFailedJob.error_details.job_params.total_pages && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Pages: </span>
                            <code className="text-gray-800">{selectedFailedJob.error_details.job_params.total_pages}</code>
                          </div>
                        )}
                        {selectedFailedJob.error_details.job_params.art_style && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Art Style: </span>
                            <code className="text-gray-800">{selectedFailedJob.error_details.job_params.art_style}</code>
                          </div>
                        )}
                        {selectedFailedJob.error_details.job_params.story_inspiration && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Inspiration: </span>
                            <code className="text-gray-800">{selectedFailedJob.error_details.job_params.story_inspiration}</code>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Advanced Mode: </span>
                          <code className="text-gray-800">{selectedFailedJob.error_details.job_params.advanced_mode ? 'Yes' : 'No'}</code>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error Type */}
                  {selectedFailedJob.error_details.error_type && (
                    <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-100 dark:border-orange-800">
                      <p className="text-xs text-orange-600 uppercase mb-2">Error Type</p>
                      <code className="text-sm text-orange-800 font-mono">{selectedFailedJob.error_details.error_type}</code>
                    </div>
                  )}

                  {/* Raw Error */}
                  {selectedFailedJob.error_details.raw_error && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">Raw Error</p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedFailedJob.error_details.raw_error);
                            setCopiedField('raw_error');
                            setTimeout(() => setCopiedField(null), 2000);
                          }}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedField === 'raw_error' ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          )}
                        </button>
                      </div>
                      <p className="text-sm text-gray-800 font-mono break-all">{selectedFailedJob.error_details.raw_error}</p>
                    </div>
                  )}

                  {/* Stack Trace */}
                  {selectedFailedJob.error_details.stack_trace && (
                    <div className="p-3 bg-gray-900 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase">Stack Trace</p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedFailedJob.error_details.stack_trace);
                            setCopiedField('stack_trace');
                            setTimeout(() => setCopiedField(null), 2000);
                          }}
                          className="p-1 hover:bg-gray-700 rounded transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedField === 'stack_trace' ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                      <pre className="text-xs text-green-400 font-mono overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto">
                        {selectedFailedJob.error_details.stack_trace}
                      </pre>
                    </div>
                  )}
                </>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-3 gap-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Created</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{formatDate(selectedFailedJob.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Started</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{selectedFailedJob.started_at ? formatDate(selectedFailedJob.started_at) : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">Failed</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{selectedFailedJob.completed_at ? formatDate(selectedFailedJob.completed_at) : '-'}</p>
                </div>
              </div>

              {/* Delete button */}
              <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => deleteFailedJob(selectedFailedJob.id)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-600 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete This Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default AdminPanel;
